import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Save, CheckCircle2, Loader2, Plus, Trash2, Home, Sparkles, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

const EMPTY = {
  property_nickname: '', property_address: '', ownership_percentage: 100, property_type: 'residential',
  ownership_structure: 'individual', purchase_date: '', purchase_price: '', rental_start_date: '',
  jointly_owned: false, other_owner_details: '', property_manager_name: '', property_manager_email: '',
  property_manager_phone: '', loan_provider: '', loan_account_number: '', sold_during_year: false,
};

const PROPERTY_TYPES = [['residential', 'Residential'], ['commercial', 'Commercial'], ['airbnb', 'Airbnb / short-stay']];
const OWNERSHIP_STRUCTURES = [['individual', 'Individual'], ['company', 'Company'], ['trust', 'Trust'], ['smsf', 'SMSF']];

// Expense categories the AI will summarise once documents are uploaded (spec summary cards).
const EXPENSE_CARDS = [
  'Advertising', 'Body corporate / strata', 'Borrowing expenses', 'Cleaning', 'Council rates', 'Gardening',
  'Insurance', 'Interest', 'Land tax', 'Legal fees', 'Pest control', 'Property agent fees',
  'Repairs & maintenance', 'Water charges', 'Depreciation', 'Capital works',
];

export default function RentalPropertiesForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [properties, setProperties] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  // Yes/No gate: null = not answered yet, true = owns rentals (reveal the register), false = none.
  const [ownsRental, setOwnsRental] = useState(null);

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const load = useCallback(async () => {
    try {
      const res = await onboardingAPI.listRentals(clientId);
      const props = res.data.properties || [];
      setProperties(props);
      // Default the gate to Yes if they already have properties on file.
      setOwnsRental((cur) => (cur === null ? (props.length > 0 ? true : null) : cur));
    } catch { /* none yet */ } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const addProperty = async () => {
    if (!draft.property_address.trim()) { toast.error('Property address is required'); return; }
    if (draft.ownership_percentage === '' || isNaN(Number(draft.ownership_percentage))) {
      toast.error('Ownership percentage is required'); return;
    }
    setBusy(true);
    try {
      await onboardingAPI.addRental(clientId, {
        ...draft,
        ownership_percentage: Number(draft.ownership_percentage),
        purchase_price: draft.purchase_price ? Number(draft.purchase_price) : null,
        purchase_date: draft.purchase_date || null,
        rental_start_date: draft.rental_start_date || null,
      });
      toast.success('Property added to your register');
      setDraft(EMPTY); setShowForm(false); await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add property');
    } finally { setBusy(false); }
  };

  const removeProperty = async (id) => {
    setBusy(true);
    try { await onboardingAPI.removeRental(clientId, id); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
    finally { setBusy(false); }
  };

  const finalize = async (status) => {
    // Marking complete requires an explicit Yes/No answer; "Yes" needs at least one property.
    if (status === 'completed') {
      if (ownsRental === null) { toast.error('Please answer whether you own any rental property'); return; }
      if (ownsRental === true && properties.length === 0) {
        toast.error('Add at least one property, or select "No" if you have none');
        return;
      }
    }
    setBusy(true);
    try {
      await onboardingAPI.finalizeRentals(clientId, { status });
      toast.success(status === 'completed' ? 'Rental register saved' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Rental Property Register</CardTitle>
          <CardDescription>Add each rental property you own. You can add as many as you need.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Yes/No gate — ticking "Yes" reveals the register inline (no navigation/lag). */}
          <div className="space-y-3">
            <Label className="text-base">Do you own any rental property?</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setOwnsRental(true)}
                className={`flex-1 rounded-lg border-2 p-3 text-sm font-medium transition ${ownsRental === true ? 'border-primary bg-blue-50 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
                data-testid="rental-owns-yes"
              >
                Yes, I own rental property
              </button>
              <button
                type="button"
                onClick={() => { setOwnsRental(false); setShowForm(false); }}
                className={`flex-1 rounded-lg border-2 p-3 text-sm font-medium transition ${ownsRental === false ? 'border-primary bg-blue-50 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
                data-testid="rental-owns-no"
              >
                No rental property
              </button>
            </div>
          </div>

          {ownsRental === false && (
            <div className="text-sm text-muted-foreground bg-muted/50 border rounded-lg p-3">
              No problem — you can mark this section complete below. If this changes, come back any time.
            </div>
          )}

          {/* Register (only when they own rental property) */}
          {ownsRental === true && <>
          {/* Existing properties */}
          {properties.map((p) => (
            <div key={p.id} className="border rounded-lg p-4" data-testid={`rental-card-${p.id}`}>
              <div className="flex items-start gap-3">
                <Home className="w-5 h-5 text-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-foreground">{p.property_nickname || p.property_address}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.property_address} · {p.ownership_percentage}% · {p.property_type}
                    {p.sold_during_year ? ' · sold this year' : ''}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeProperty(p.id)} disabled={busy} title="Remove">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>

              {/* AI extraction + review status placeholders (spec) */}
              <div className="mt-3 flex items-center gap-2 text-xs">
                <Badge className="bg-muted text-muted-foreground">Documents pending</Badge>
                <span className="text-muted-foreground/70">→</span>
                <Badge className="bg-muted text-muted-foreground">AI extracted</Badge>
                <Badge className="bg-muted text-muted-foreground">Accountant reviewed</Badge>
                <Badge className="bg-muted text-muted-foreground">Ready for tax return</Badge>
              </div>
            </div>
          ))}

          {/* Add-property form */}
          {showForm ? (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Property nickname</Label><Input value={draft.property_nickname} onChange={onInput('property_nickname')} placeholder="e.g. Sydney unit" /></div>
                <div className="space-y-2"><Label>Property address *</Label><Input value={draft.property_address} onChange={onInput('property_address')} data-testid="rental-address" /></div>
                <div className="space-y-2"><Label>Ownership % *</Label><Input type="number" value={draft.ownership_percentage} onChange={onInput('ownership_percentage')} data-testid="rental-ownership" /></div>
                <div className="space-y-2">
                  <Label>Property type *</Label>
                  <Select value={draft.property_type} onValueChange={set('property_type')}>
                    <SelectTrigger data-testid="rental-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROPERTY_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ownership structure</Label>
                  <Select value={draft.ownership_structure} onValueChange={set('ownership_structure')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{OWNERSHIP_STRUCTURES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date purchased</Label><Input type="date" value={draft.purchase_date} onChange={onInput('purchase_date')} /></div>
                <div className="space-y-2"><Label>Purchase price</Label><Input type="number" value={draft.purchase_price} onChange={onInput('purchase_price')} /></div>
                <div className="space-y-2"><Label>Date first available for rent</Label><Input type="date" value={draft.rental_start_date} onChange={onInput('rental_start_date')} /></div>
                <div className="space-y-2"><Label>Property manager name</Label><Input value={draft.property_manager_name} onChange={onInput('property_manager_name')} /></div>
                <div className="space-y-2"><Label>Property manager email</Label><Input type="email" value={draft.property_manager_email} onChange={onInput('property_manager_email')} /></div>
                <div className="space-y-2"><Label>Property manager phone</Label><Input value={draft.property_manager_phone} onChange={onInput('property_manager_phone')} /></div>
                <div className="space-y-2"><Label>Loan provider</Label><Input value={draft.loan_provider} onChange={onInput('loan_provider')} /></div>
                <div className="space-y-2"><Label>Loan account number</Label><Input value={draft.loan_account_number} onChange={onInput('loan_account_number')} /></div>
                <div className="space-y-2 md:col-span-2"><Label>Other owner details (if jointly owned)</Label><Input value={draft.other_owner_details} onChange={onInput('other_owner_details')} /></div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={draft.jointly_owned} onCheckedChange={(v) => set('jointly_owned')(!!v)} /> <span className="text-sm text-foreground/90">Jointly owned</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={draft.sold_during_year} onCheckedChange={(v) => set('sold_during_year')(!!v)} /> <span className="text-sm text-foreground/90">Sold during the year</span></label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setShowForm(false); setDraft(EMPTY); }}>Cancel</Button>
                <Button onClick={addProperty} disabled={busy} data-testid="rental-add-submit"><Plus className="w-4 h-4 mr-1" /> Add property</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setShowForm(true)} data-testid="rental-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add a rental property
            </Button>
          )}

          {/* Spec placeholders: AI extraction + expense summary */}
          <div className="flex items-start gap-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
            AI will review your uploaded rental documents and summarise income, expenses and key tax information.
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Expense summary (populated after document upload &amp; AI extraction)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {EXPENSE_CARDS.map((c) => (
                <div key={c} className="rounded-lg border bg-muted/50 p-2 text-center">
                  <div className="text-[11px] text-muted-foreground">{c}</div>
                  <div className="text-sm font-medium text-muted-foreground/70">—</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-2">Document uploads for each property (rental statements, rates, interest, etc.) will be available in the Document Centre.</p>
          </div>
          </>}

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => finalize('in_progress')} disabled={busy} data-testid="rental-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => finalize('completed')} disabled={busy} data-testid="rental-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
